import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DEAL_STAGES, formatMoney, stageProbability, type DealStage } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent, useDraggable, useDroppable,
} from "@dnd-kit/core";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface Deal {
  id: string; title: string; value_cents: number; currency: string;
  stage: DealStage; owner_id: string | null; expected_close_at: string | null;
  notes: string | null; contact_id: string | null;
  contacts?: { name: string; organisation: string | null } | null;
}

function DealCard({ deal, onClick }: { deal: Deal; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`bg-card border border-border rounded p-3 mb-2 cursor-grab active:cursor-grabbing hover:border-primary/50 transition ${isDragging ? "opacity-40" : ""}`}
    >
      <div className="font-medium text-sm text-foreground line-clamp-2">{deal.title}</div>
      {deal.contacts ? (
        <div className="text-xs text-muted-foreground mt-1 truncate">
          <span className="text-foreground/80">{deal.contacts.name}</span>
          {deal.contacts.organisation && <span> · {deal.contacts.organisation}</span>}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground/60 italic mt-1">No customer linked</div>
      )}
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs font-semibold text-primary">{formatMoney(deal.value_cents, deal.currency)}</span>
        {deal.expected_close_at && (
          <span className="text-[10px] text-muted-foreground">{format(new Date(deal.expected_close_at), "d MMM")}</span>
        )}
      </div>
    </div>
  );
}

function StageColumn({ stageId, label, deals, onCardClick }: {
  stageId: DealStage; label: string; deals: Deal[]; onCardClick: (d: Deal) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stageId });
  const total = deals.reduce((a, d) => a + (d.value_cents ?? 0), 0);
  const weighted = total * stageProbability(stageId);
  return (
    <div ref={setNodeRef} className={`flex-1 min-w-[260px] bg-muted/20 rounded p-2 ${isOver ? "ring-2 ring-primary" : ""}`}>
      <div className="px-2 py-1 mb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{label}</h3>
          <span className="text-xs text-muted-foreground">{deals.length}</span>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {formatMoney(total)} · w. {formatMoney(weighted)}
        </div>
      </div>
      <div className="min-h-[100px]">
        {deals.map(d => <DealCard key={d.id} deal={d} onClick={() => onCardClick(d)} />)}
      </div>
    </div>
  );
}

export default function AdminDeals() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [creating, setCreating] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    const { data, error } = await supabase
      .from("deals")
      .select("*, contacts(name, organisation)")
      .is("archived_at", null)
      .order("updated_at", { ascending: false });
    if (error) { toast.error(error.message); return; }
    setDeals(data as any);
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    if (!e.over) return;
    const newStage = String(e.over.id) as DealStage;
    const dealId = String(e.active.id);
    const deal = deals.find(d => d.id === dealId);
    if (!deal || deal.stage === newStage) return;
    setDeals(ds => ds.map(d => d.id === dealId ? { ...d, stage: newStage } : d));
    const { error } = await supabase.from("deals").update({ stage: newStage }).eq("id", dealId);
    if (error) { toast.error(error.message); load(); }
  };

  const active = deals.find(d => d.id === activeId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Deals</h1>
        <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-2" />Add deal</Button>
      </div>
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {DEAL_STAGES.map(st => (
            <StageColumn
              key={st.id}
              stageId={st.id}
              label={st.label}
              deals={deals.filter(d => d.stage === st.id)}
              onCardClick={setEditing}
            />
          ))}
        </div>
        <DragOverlay>
          {active && (
            <div className="bg-card border border-primary rounded p-3 shadow-lg w-64">
              <div className="font-medium text-sm">{active.title}</div>
              <div className="text-xs text-primary mt-1">{formatMoney(active.value_cents, active.currency)}</div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <DealEditor
        deal={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        onArchived={() => { setEditing(null); load(); }}
      />
      <DealEditor
        deal={creating ? ({ id: "", title: "", value_cents: 0, currency: "GBP", stage: "lead_generation", owner_id: null, expected_close_at: null, notes: null, contact_id: null } as Deal) : null}
        isNew
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }}
      />
    </div>
  );
}

function DealEditor({ deal, onClose, onSaved, onArchived, isNew }: {
  deal: Deal | null; onClose: () => void; onSaved: () => void; onArchived?: () => void; isNew?: boolean;
}) {
  const [form, setForm] = useState<Deal | null>(deal);
  const [contacts, setContacts] = useState<{ id: string; name: string; organisation: string | null }[]>([]);
  useEffect(() => setForm(deal), [deal]);
  useEffect(() => {
    if (!deal) return;
    supabase.from("contacts").select("id, name, organisation").is("archived_at", null).order("name")
      .then(({ data }) => setContacts(data ?? []));
  }, [deal]);
  if (!form) return null;

  const save = async () => {
    const payload = {
      title: form.title, value_cents: Number(form.value_cents) || 0, currency: form.currency,
      stage: form.stage, expected_close_at: form.expected_close_at, notes: form.notes,
      contact_id: form.contact_id,
    };
    if (isNew) {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from("deals").insert({ ...payload, owner_id: user?.id ?? null });
      if (error) { toast.error(error.message); return; }
    } else {
      const { error } = await supabase.from("deals").update(payload).eq("id", form.id);
      if (error) { toast.error(error.message); return; }
    }
    toast.success("Saved");
    onSaved();
  };

  const archive = async () => {
    const { error } = await supabase.from("deals").update({ archived_at: new Date().toISOString() }).eq("id", form.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Archived");
    onArchived?.();
  };

  return (
    <Sheet open={!!deal} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle>{isNew ? "New deal" : "Edit deal"}</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Value</Label>
              <Input type="number" value={(form.value_cents ?? 0) / 100} onChange={(e) => setForm({ ...form, value_cents: Math.round(Number(e.target.value) * 100) })} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Customer</Label>
            <Select
              value={form.contact_id ?? "__none__"}
              onValueChange={(v) => setForm({ ...form, contact_id: v === "__none__" ? null : v })}
            >
              <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {contacts.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.organisation ? ` — ${c.organisation}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stage</Label>
            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v as DealStage })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEAL_STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Expected close</Label>
            <Input type="date" value={form.expected_close_at ?? ""} onChange={(e) => setForm({ ...form, expected_close_at: e.target.value || null })} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={4} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} className="flex-1">Save</Button>
            {!isNew && <Button variant="outline" onClick={archive}>Archive</Button>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
